import React, { useEffect, useState } from 'react';
import styled from 'astroturf/react';

import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import trackClick from 'js/utils/trackClick';

import { dotVisible } from '../acknowledgment';
import { impressionExtra, statDataState } from '../arms';
import { citationsCell } from '../citationsCell';
import type { NavImpactSurfaces, StatStyle } from '../arms';
import type { ImpactStats } from '../../types';
import ImpactStatTile, { STACKED_TILE_WIDTH } from '../ImpactStatTile';
import { trendAdornment } from '../ornaments/TrendAdornment';

const UNAVAILABLE_AFTER_MS = 50000;

const Cell = styled.div<{
  statStyle: StatStyle;
  tint: 'white' | 'beige';
  interactive?: boolean;
}>`
  display: flex;
  flex: 0 0 auto;
  box-sizing: border-box;
  align-items: center;
  height: 100%;

  &.tint-white {
    border-right: 1px solid var(--stroke-light, #dddde2);
  }

  &.tint-beige {
    border-right: 1px solid var(--stroke-beige-light, #ccc9bc);
  }

  &.interactive:hover {
    background: var(--background-light-blue, #eef2f9);
  }
`;

const CellTile = styled(ImpactStatTile)<{
  cellStyle?: 'delta_chip';
}>`
  box-sizing: content-box;
  padding: 0 16px;

  &.cellStyle-delta_chip {
    align-items: flex-end;
    padding: 0 18px 8px 16px;
  }
`;

const Row = styled.div`
  display: flex;
  flex: 0 0 auto;
  height: 100%;
`;

const RowWatcher = styled(ImpressionWatcher)`
  display: flex;
  flex: 0 0 auto;
  height: 100%;
`;

type CellSpec = {
  key: string;
  count: number | null;
  label: string;
  trendPct: number | null;
  unseen: boolean | undefined;
  seenValue: number | null;
  href: string;
  source: string;
};

export type StatCellsProps = {
  arm: string | null | undefined;
  surfaces: NavImpactSurfaces;
  impactStats?: ImpactStats | null;
  tint: 'white' | 'beige';
  citationsOnly?: boolean;
  testRunning?: boolean;
  hasUnseenMentions?: boolean;
  hasUnseenReaders?: boolean;
  citationsHref: string;
  paperViewsHref: string;
};

const StatCells = ({
  arm,
  surfaces,
  impactStats,
  tint,
  citationsOnly = false,
  testRunning = false,
  hasUnseenMentions,
  hasUnseenReaders,
  citationsHref,
  paperViewsHref,
}: StatCellsProps) => {
  const [waitedOut, setWaitedOut] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(
      () => setWaitedOut(true),
      UNAVAILABLE_AFTER_MS,
    );
    return () => window.clearTimeout(timer);
  }, []);

  const bucket = arm ?? 'control';
  const firstCell = citationsCell(impactStats);
  const paperViewsTotal = impactStats?.paperViewsTotal ?? null;

  const paperViewsCell: CellSpec = {
    key: 'paper-views',
    count: paperViewsTotal,
    label: 'Paper views',
    trendPct: impactStats?.paperViewsTrendPct ?? null,
    unseen: hasUnseenReaders,
    seenValue: paperViewsTotal,
    href: paperViewsHref,
    source: 'nav-bar--impact-paper-views',
  };

  const cells: CellSpec[] = [
    {
      key: 'citations',
      count: firstCell.count,
      label: firstCell.label,
      trendPct: firstCell.trendPct,
      unseen: hasUnseenMentions,
      seenValue: firstCell.count,
      href: citationsHref,
      source: 'nav-bar--impact-citations',
    },
    ...(citationsOnly ? [] : [paperViewsCell]),
  ];

  const dataState = statDataState(cells.map((cell) => cell.count));

  const row = (
    <Row
      data-testid="impact-stat-cells"
      style={
        {
          '--skeleton-width': `${STACKED_TILE_WIDTH}px`,
        } as React.CSSProperties
      }
    >
      {cells.map((cell) => {
        const loading = cell.count == null && !waitedOut;
        return (
          <Cell
            key={cell.key}
            statStyle={surfaces.statStyle}
            tint={tint}
            interactive={!loading}
            data-testid={`impact-stat-cell-${cell.key}`}
          >
            <CellTile
              cellStyle={
                surfaces.statStyle === 'delta_chip' ? 'delta_chip' : undefined
              }
              count={loading ? null : (cell.count ?? 0)}
              loading={loading}
              label={cell.label}
              statStyle={surfaces.statStyle}
              adornment={trendAdornment(surfaces.statStyle, cell.trendPct)}
              href={cell.href}
              onClick={() => {
                trackClick({ source: cell.source, bucket });
              }}
              showDot={
                cell.count != null && dotVisible(cell.unseen, cell.seenValue)
              }
            />
          </Cell>
        );
      })}
    </Row>
  );

  if (firstCell.count == null && !waitedOut) {
    return row;
  }

  return (
    <RowWatcher
      source="nav_impact_stats.strip"
      extra={{
        ...impressionExtra(arm, testRunning, dataState),
        citations_cell: firstCell.metric,
      }}
    >
      {row}
    </RowWatcher>
  );
};

export default StatCells;
